import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
} from 'recharts'
import { Search, Info, Plus, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { ItemSalesRow, Tag } from '@/types'
import { ExportButton } from './ExportButton'
import { ReportsExportSlot } from './ReportsExportSlot'
import { TagManagerDialog } from './TagManagerDialog'
import { openPrintableReport, escapeHtml } from '@/lib/printReport'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { getStoredUserRole } from '@/lib/staff-roles'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import {
  displayLabelForOrderType,
  colorForOrderTypeId,
  normalizeOrderTypeId,
} from '@/lib/orderTypeSettings'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatDateRangeHuman, cn } from '@/lib/utils'
import { chartTooltipBar } from '@/lib/chartTheme'

// Sort options grow with the projected-margin feature: 'margin' = absolute
// projected gross profit (PKR), 'margin_pct' = margin as % of net sales.
// Items missing a projected_cost are sorted to the bottom by the backend.
type SortKey = 'qty' | 'gross' | 'net' | 'margin' | 'margin_pct'

// group_by turns this into the unified "Menu Performance" report: per-item
// (default), the category rollup, or per-tag segments with share-of-category.
type GroupBy = 'item' | 'category' | 'tag'

const SORT_LABELS: Record<SortKey, string> = {
  net: 'Net',
  gross: 'Gross',
  qty: 'Quantity',
  margin: 'Projected margin',
  margin_pct: 'Margin %',
}

const GROUP_LABELS: Record<GroupBy, string> = {
  item: 'Item',
  category: 'Category',
  tag: 'Tag',
}

interface Props {
  range: UseReportRange
}

interface Column {
  key: string
  label: string
  align: 'left' | 'right'
  render: (row: ItemSalesRow) => ReactNode
}

export function ItemsTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<string>('all')
  const [tagId, setTagId] = useState<string>('all')
  const [groupBy, setGroupBy] = useState<GroupBy>('item')
  const [sortKey, setSortKey] = useState<SortKey>('net')
  // F-RPT-N9 channel axis. `channelIds` empty = every channel (the "All" chip).
  // `orderSource` is a SEPARATE axis: an online delivery order matches both
  // Delivery and Online, so the two can never be merged into one chip row.
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [orderSource, setOrderSource] = useState<'all' | 'pos' | 'server' | 'online'>('all')

  // Tag CRUD lives here now (not on the Menu page). Only admin/manager may
  // mutate — accountants can view the tag report but the backend never grants
  // them the tag write endpoints, so we hide the dead controls.
  const canManageTags = ['admin', 'manager'].includes(getStoredUserRole())
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null)

  // Shared ['categories'] cache via the single-source-of-truth hook (F-FE-N6):
  // always resolves to Category[], so no consumer can poison the shape.
  const categoriesQuery = useCategories()
  const categories = categoriesQuery.data ?? []

  // Active tags for the Tag group/filter dropdown (accountant-safe endpoint).
  const tagsQuery = useQuery({
    queryKey: ['report-tags'],
    queryFn: () => apiClient.getReportTags(),
    staleTime: 60_000,
  })
  const tags = (tagsQuery.data?.data as Tag[] | undefined) ?? []
  const hasTags = tags.length > 0

  const refreshTags = () => {
    queryClient.invalidateQueries({ queryKey: ['report-tags'] })
    queryClient.invalidateQueries({ queryKey: ['reports-v2'] })
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteTag(id),
    onSuccess: (res) => {
      if (!res.success) {
        toastHelpers.error('Delete tag', res.message || 'Failed to delete tag')
        return
      }
      refreshTags()
      toastHelpers.success('Tag deleted', deleteTarget?.name ?? '')
      setDeleteTarget(null)
    },
    onError: (err: unknown) => toastHelpers.apiError('Delete tag', err),
  })

  const openCreateTag = () => {
    setEditingTag(null)
    setTagDialogOpen(true)
  }
  const openEditTag = (tag: Tag) => {
    setEditingTag(tag)
    setTagDialogOpen(true)
  }

  const itemsQuery = useQuery({
    queryKey: [
      'reports-v2',
      'items',
      range.fromISO,
      range.toISO,
      search,
      categoryId,
      tagId,
      groupBy,
      sortKey,
      channelIds.join(','),
      orderSource,
    ],
    queryFn: () =>
      apiClient.getItemSalesReport(range.fromISO, range.toISO, {
        search: search.trim() || undefined,
        category_id: categoryId === 'all' ? undefined : categoryId,
        tag_id: tagId === 'all' ? undefined : tagId,
        group_by: groupBy,
        sort: sortKey,
        limit: 1000,
        order_types: channelIds,
        order_source: orderSource === 'all' ? undefined : orderSource,
      }),
    staleTime: 30_000,
  })

  const items = itemsQuery.data?.data ?? []
  const totals = useMemo(
    () =>
      items.reduce(
        (acc, r) => ({
          qty: acc.qty + r.qty_sold,
          net: acc.net + r.net,
          // Margin / coverage totals are computed only across rows that HAVE a
          // cost set; rows without are surfaced separately so the user can see
          // honest coverage rather than a misleading "PKR 0 margin" tally.
          margin: acc.margin + (r.projected_margin ?? 0),
          // Coverage follows the COGS figure itself, not the reference unit
          // cost: margin is now the sale-time snapshot (same basis as Overview),
          // which a recipe-costed item carries even with no projected_cost set.
          coveredNet: acc.coveredNet + (r.projected_cogs != null ? r.net : 0),
          withCost: acc.withCost + (r.projected_cogs != null ? 1 : 0),
        }),
        { qty: 0, net: 0, margin: 0, coveredNet: 0, withCost: 0 },
      ),
    [items],
  )
  const coveragePct = totals.net > 0 ? (totals.coveredNet / totals.net) * 100 : 0
  const top = useMemo(() => items.slice(0, 10), [items])

  // ── F-RPT-N9: the channel axis ────────────────────────────────────────────
  // Channels are DATA-DRIVEN, never a hardcoded enum: a chip or a column exists
  // only where the tenant actually moved units. A dine-in-only café sees two
  // columns, not four with two zeros.
  //
  // Chips need a wider memory than the current response, though — once you
  // filter to Delivery the response only contains delivery, and a chip row
  // rebuilt from that alone would strand you with no way back. So observed
  // channels accumulate across requests, reset whenever the date range changes
  // (a channel that only existed in July must not linger into August).
  const rangeKey = `${range.fromISO}|${range.toISO}`
  const observedRef = useRef<{ key: string; ids: Set<string>; online: boolean }>({
    key: '',
    ids: new Set(),
    online: false,
  })
  if (observedRef.current.key !== rangeKey) {
    observedRef.current = { key: rangeKey, ids: new Set(), online: false }
  }
  for (const row of items) {
    for (const id of Object.keys(row.qty_by_channel ?? {})) observedRef.current.ids.add(id)
    if ((row.qty_online ?? 0) > 0) observedRef.current.online = true
  }

  const { raw: orderTypeRows } = useEnabledOrderTypes()

  // Settings order first (that's the order the counter shows them in), then any
  // channel present in the data but absent from settings — a disabled-but-
  // historical type, or a legacy 'other'/'takeaway' row — appended alphabetically.
  const orderChannels = useMemo(() => {
    const configured = orderTypeRows.map((r) => normalizeOrderTypeId(r.id))
    return (ids: Iterable<string>) => {
      const pool = new Set(ids)
      const out: string[] = []
      for (const id of configured) {
        if (pool.delete(id)) out.push(id)
      }
      return [...out, ...Array.from(pool).sort()]
    }
  }, [orderTypeRows])

  // Chip options. Tag mode returns no breakdown (its tag×category shape has no
  // single owner per unit), so fall back to the configured set there rather than
  // hiding the filter that still works.
  const channelOptions = useMemo(() => {
    const pool = new Set<string>(observedRef.current.ids)
    channelIds.forEach((id) => pool.add(id))
    if (pool.size === 0) {
      orderTypeRows.filter((r) => r.enabled !== false).forEach((r) => pool.add(normalizeOrderTypeId(r.id)))
    }
    return orderChannels(pool)
    // observedRef mutates in render above; `items` is the signal that it changed.
  }, [items, channelIds, orderTypeRows, orderChannels])

  // Breakdown columns come from the CURRENT rows only — unlike the chips, these
  // must not remember channels that the active filter excluded.
  const channelColumns = useMemo(() => {
    const present = new Set<string>()
    for (const row of items) {
      for (const [id, qty] of Object.entries(row.qty_by_channel ?? {})) {
        if (qty > 0) present.add(id)
      }
    }
    // A single-channel split is just the Qty column again — don't waste the width.
    return present.size > 1 ? orderChannels(present) : []
  }, [items, orderChannels])

  const showOnlineColumn = useMemo(() => items.some((r) => (r.qty_online ?? 0) > 0), [items])
  // The POS/Server/Online axis only earns screen space once the tenant has
  // actually taken a storefront order in this window.
  const showSourceFilter = observedRef.current.online || orderSource !== 'all'

  // displayLabelForOrderType falls back to the raw id for anything not in
  // settings ('other', a legacy 'takeaway'), which reads as lowercase in a chip.
  const channelLabel = useCallback(
    (id: string) => {
      const label = displayLabelForOrderType(id, orderTypeRows)
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    [orderTypeRows],
  )

  const toggleChannel = (id: string) =>
    setChannelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  const channelFilterActive = channelIds.length > 0 || orderSource !== 'all'

  // Tag rows can overlap (an item may carry several tags), so summing qty/net
  // across them is not a meaningful grand total — hide the totals badges there.
  const showTotals = groupBy !== 'tag'

  const columns = useMemo(
    () =>
      buildColumns(groupBy, formatCurrency, {
        channels: channelColumns,
        labelFor: channelLabel,
        showOnline: showOnlineColumn,
      }),
    [groupBy, formatCurrency, channelColumns, channelLabel, showOnlineColumn],
  )

  const brand = useBusinessNameWithFallback()
  const handlePrintPdf = () => {
    openPrintableReport({
      title: `Menu Performance — by ${GROUP_LABELS[groupBy]}`,
      subtitle: `${formatDateRangeHuman(range.from, range.to)} • Asia/Karachi`,
      bodyHtml: buildItemsPdf(items, totals, formatCurrency, groupBy, {
        channels: channelColumns,
        labelFor: channelLabel,
      }),
      brand,
    })
  }

  const exportExtraParams = useMemo(() => {
    const x: Record<string, string> = { group_by: groupBy }
    if (search.trim()) x.search = search.trim()
    if (categoryId !== 'all') x.category_id = categoryId
    if (tagId !== 'all') x.tag_id = tagId
    // Channel filters must reach the CSV too, or the export silently reports a
    // wider window than the table it was exported from.
    if (channelIds.length > 0) x.order_types = channelIds.join(',')
    if (orderSource !== 'all') x.order_source = orderSource
    return x
  }, [search, categoryId, tagId, groupBy, channelIds, orderSource])

  const searchPlaceholder =
    groupBy === 'category' ? 'Search category…' : groupBy === 'tag' ? 'Search tag…' : 'Search item name…'

  return (
    <div className="space-y-4">
      {/* Group-by segmented control — the always-visible signal that you can
          slice by Category or Tag, not just per item. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Group by</span>
          <div className="inline-flex items-center rounded-lg border bg-muted/40 p-0.5 text-sm">
            {(['item', 'category', 'tag'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupBy(g)}
                aria-pressed={groupBy === g}
                className={cn(
                  'rounded-md px-3 py-1.5 transition',
                  groupBy === g ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {GROUP_LABELS[g]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Channel filter — which service channel the units went out through.
          Only channels the tenant actually uses appear; nothing renders as an
          empty chip. Multi-select: picking Takeaway + Delivery answers "what
          travels well". */}
      {channelOptions.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Channel</span>
          <div className="inline-flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setChannelIds([])}
              aria-pressed={channelIds.length === 0}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition',
                channelIds.length === 0
                  ? 'border-foreground/20 bg-foreground/5 font-medium'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              All
            </button>
            {channelOptions.map((id) => {
              const active = channelIds.includes(id)
              const color = colorForOrderTypeId(orderTypeRows, id)
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggleChannel(id)}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition',
                    active
                      ? 'border-foreground/20 bg-foreground/5 font-medium'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {color && (
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} aria-hidden />
                  )}
                  {channelLabel(id)}
                </button>
              )
            })}
          </div>
          {showSourceFilter && (
            <Select value={orderSource} onValueChange={(v) => setOrderSource(v as typeof orderSource)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any source</SelectItem>
                <SelectItem value="pos">Counter (POS)</SelectItem>
                <SelectItem value="server">Server / tablet</SelectItem>
                <SelectItem value="online">Online storefront</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* % of net is computed against the FILTERED grand total, so under a
          channel filter it reads "% of that channel's net" — say so rather than
          let the number quietly change meaning. */}
      {channelFilterActive && (
        <p className="text-xs text-muted-foreground">
          Showing one slice of your sales — “% of net” is measured against the filtered total, not all sales.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasTags ? (
          <Select value={tagId} onValueChange={setTagId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All tags" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {tags.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-xs text-muted-foreground">No tags yet — create one to segment reports.</span>
        )}
        <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
          <SelectTrigger className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="net">Sort: Net sales</SelectItem>
            <SelectItem value="gross">Sort: Gross sales</SelectItem>
            <SelectItem value="qty">Sort: Quantity sold</SelectItem>
            <SelectItem value="margin">Sort: Projected margin (PKR)</SelectItem>
            <SelectItem value="margin_pct">Sort: Margin %</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <ReportsExportSlot>
        <ExportButton
          report="items"
          reportLabel="Menu performance"
          fromISO={range.fromISO}
          toISO={range.toISO}
          extraParams={exportExtraParams}
          onPrintPdf={handlePrintPdf}
        />
      </ReportsExportSlot>

      {/* Tag-mode management panel — create/edit/delete tags right where you
          read the numbers. Mutating controls are admin/manager only. */}
      {groupBy === 'tag' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-medium text-foreground">
                  Tag segments {hasTags ? `(${tags.length})` : ''}
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  Group a subset of items into a promo/segment without changing their category — totals stay the same.
                </p>
              </div>
              {canManageTags && (
                <Button onClick={openCreateTag} className="gap-2">
                  <Plus className="h-4 w-4" />
                  New Tag
                </Button>
              )}
            </div>

            {hasTags ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {tags.map((t) => (
                  <div
                    key={t.id}
                    className="inline-flex items-center gap-2 rounded-full border bg-muted/30 py-1 pl-2.5 pr-1.5 text-sm"
                  >
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: t.color || '#6B7280' }}
                    />
                    <span className="font-medium">{t.name}</span>
                    {canManageTags && (
                      <span className="flex items-center">
                        <button
                          type="button"
                          aria-label={`Edit ${t.name}`}
                          onClick={() => openEditTag(t)}
                          className="rounded p-1 text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${t.name}`}
                          onClick={() => setDeleteTarget(t)}
                          className="rounded p-1 text-muted-foreground hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  {canManageTags
                    ? 'No tags yet. Create one like "Summer Special", tick a few items, and they show below as their own segment with a share-of-category figure.'
                    : 'No tags yet. Ask an admin or manager to create one to segment this report.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top 10 by {SORT_LABELS[sortKey].toLowerCase()}</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px]">
          {itemsQuery.isLoading ? (
            <Skeleton className="w-full h-full" />
          ) : top.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              No sales in this range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top} layout="vertical" margin={{ top: 16, right: 24, left: 16, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) =>
                    sortKey === 'qty'
                      ? String(v)
                      : sortKey === 'margin_pct'
                      ? `${(v ?? 0).toFixed(0)}%`
                      : formatCompact(v)
                  }
                />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={140} />
                <ReTooltip
                  {...chartTooltipBar}
                  formatter={(v: number) => {
                    if (sortKey === 'qty') return [v, 'Qty']
                    if (sortKey === 'margin_pct') return [`${(v ?? 0).toFixed(1)}%`, 'Margin %']
                    if (sortKey === 'margin') return [formatCurrency(v), 'Projected margin']
                    return [formatCurrency(v), sortKey === 'gross' ? 'Gross' : 'Net']
                  }}
                />
                <Bar
                  dataKey={
                    sortKey === 'qty'
                      ? 'qty_sold'
                      : sortKey === 'margin'
                      ? 'projected_margin'
                      : sortKey === 'margin_pct'
                      ? 'projected_margin_pct'
                      : sortKey
                  }
                  fill="#0ea5e9"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex flex-wrap items-center justify-between gap-2">
            <span>
              {groupBy === 'category' ? 'Categories' : groupBy === 'tag' ? 'Segments' : 'All items'} ({items.length})
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {showTotals && totals.qty > 0 && (
                <Badge variant="outline" className="text-xs">
                  Total qty: {totals.qty.toLocaleString('en-US')} • Net: {formatCurrency(totals.net)}
                </Badge>
              )}
              {showTotals && totals.coveredNet > 0 && (
                <Badge variant="outline" className="text-xs">
                  Projected margin: {formatCurrency(totals.margin)} • {coveragePct.toFixed(0)}% of net covered
                </Badge>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {itemsQuery.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No results match your filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} className={cn('px-4 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((row) => (
                    <tr key={row.key_id ?? row.product_id} className="hover:bg-muted/20">
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={cn('px-4 py-3', col.align === 'right' ? 'text-right tabular-nums' : '')}
                        >
                          {col.render(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit tag (with item picker) */}
      {canManageTags && (
        <TagManagerDialog
          open={tagDialogOpen}
          onOpenChange={setTagDialogOpen}
          editing={editingTag}
          onSaved={refreshTags}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !deleteMutation.isPending && !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete tag?</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.name}" will be removed from every item it tags. This only affects tag reporting —
              categories and sales totals are unchanged.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// buildColumns returns the adaptive column set for the active grouping. Item
// mode preserves the legacy per-product columns; category/tag modes drop the
// per-unit cost (meaningless for a group) and tag mode swaps "% of net" for the
// "% of category" share figure and adds an item-count column.
//
// F-RPT-N9: the per-channel qty columns are spliced in right after Qty, so the
// eye reads "31 total = 18 + 9 + 4" left to right. They are generated from the
// channels present in the data (`opts.channels`) rather than a fixed list —
// a tenant that never does delivery gets no delivery column at all.
function buildColumns(
  groupBy: GroupBy,
  formatCurrency: (n: number) => string,
  opts: { channels: string[]; labelFor: (id: string) => string; showOnline: boolean },
): Column[] {
  const money = (v: number) => formatCurrency(v)
  const nullableMoney = (v: number | null | undefined) =>
    v != null ? formatCurrency(v) : <span className="text-muted-foreground/60">—</span>

  const qty: Column = { key: 'qty', label: 'Qty', align: 'right', render: (r) => r.qty_sold.toLocaleString('en-US') }

  // Channel columns are mutually exclusive and sum to Qty. "Online" is
  // deliberately styled apart and labelled as a subset — an online delivery
  // order is counted under Delivery AND here, so adding it to the others would
  // double-count.
  const channelCols: Column[] =
    groupBy === 'tag'
      ? []
      : [
          ...opts.channels.map<Column>((id) => ({
            key: `ch_${id}`,
            label: opts.labelFor(id),
            align: 'right',
            render: (r) => {
              const v = r.qty_by_channel?.[id] ?? 0
              return v > 0 ? (
                v.toLocaleString('en-US')
              ) : (
                <span className="text-muted-foreground/40">—</span>
              )
            },
          })),
          ...(opts.showOnline
            ? [
                {
                  key: 'ch_online',
                  label: 'of which online',
                  align: 'right' as const,
                  render: (r: ItemSalesRow) => {
                    const v = r.qty_online ?? 0
                    return (
                      <span
                        className="text-muted-foreground"
                        title="Units ordered through the storefront. Already counted in the channel columns above — not an extra channel."
                      >
                        {v > 0 ? v.toLocaleString('en-US') : '—'}
                      </span>
                    )
                  },
                },
              ]
            : []),
        ]
  const avg: Column = {
    key: 'avg',
    label: 'Avg unit price',
    align: 'right',
    render: (r) => <span className="text-muted-foreground">{money(r.avg_unit_price)}</span>,
  }
  const gross: Column = { key: 'gross', label: 'Gross', align: 'right', render: (r) => money(r.gross) }
  const net: Column = {
    key: 'net',
    label: 'Net',
    align: 'right',
    render: (r) => <span className="font-semibold">{money(r.net)}</span>,
  }
  const pctNet: Column = {
    key: 'pct',
    label: '% of net',
    align: 'right',
    render: (r) => <span className="text-muted-foreground">{r.percent_of_net.toFixed(1)}%</span>,
  }
  const orders: Column = {
    key: 'orders',
    label: 'Orders',
    align: 'right',
    render: (r) => <span className="text-muted-foreground">{r.orders_count.toLocaleString('en-US')}</span>,
  }
  const items: Column = {
    key: 'items',
    label: 'Items',
    align: 'right',
    render: (r) => <span className="text-muted-foreground">{(r.item_count ?? 0).toLocaleString('en-US')}</span>,
  }
  const projMargin: Column = {
    key: 'proj_margin',
    label: 'Proj. margin',
    align: 'right',
    render: (r) => {
      const neg = r.projected_margin != null && r.projected_margin < 0
      return <span className={neg ? 'text-destructive' : ''}>{nullableMoney(r.projected_margin)}</span>
    },
  }
  const marginPct: Column = {
    key: 'margin_pct',
    label: 'Margin %',
    align: 'right',
    render: (r) => {
      const neg = r.projected_margin != null && r.projected_margin < 0
      return (
        <span className={neg ? 'text-destructive' : 'text-muted-foreground'}>
          {r.projected_margin_pct != null ? `${r.projected_margin_pct.toFixed(1)}%` : <span className="text-muted-foreground/60">—</span>}
        </span>
      )
    },
  }

  const categoryCol: Column = {
    key: 'category',
    label: 'Category',
    align: 'left',
    render: (r) => <span className="text-muted-foreground">{r.category ?? '—'}</span>,
  }

  if (groupBy === 'category') {
    const nameCol: Column = { key: 'name', label: 'Category', align: 'left', render: (r) => <span className="font-medium">{r.name}</span> }
    const projCogs: Column = {
      key: 'proj_cogs',
      label: 'Proj. COGS',
      align: 'right',
      render: (r) => <span className="text-muted-foreground">{nullableMoney(r.projected_cogs)}</span>,
    }
    return [nameCol, items, qty, ...channelCols, avg, gross, net, pctNet, orders, projCogs, projMargin, marginPct]
  }

  if (groupBy === 'tag') {
    const nameCol: Column = { key: 'name', label: 'Tag', align: 'left', render: (r) => <span className="font-medium">{r.name}</span> }
    const shareCol: Column = {
      key: 'share',
      label: '% of category',
      align: 'right',
      render: (r) =>
        r.share_of_category != null ? (
          <span className="font-medium">{r.share_of_category.toFixed(1)}%</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        ),
    }
    return [nameCol, categoryCol, items, qty, gross, net, shareCol, orders, projMargin, marginPct]
  }

  // item (default) — legacy per-product columns, incl. the per-unit cost and
  // the "No cost set" coverage flag on the name cell.
  const nameCol: Column = {
    key: 'name',
    label: 'Item',
    align: 'left',
    render: (r) => (
      <div className="flex items-center gap-2 font-medium">
        <span>{r.name}</span>
        {r.projected_cost == null && (
          <Badge variant="outline" className="border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300">
            No cost set
          </Badge>
        )}
      </div>
    ),
  }
  const projCost: Column = {
    key: 'proj_cost',
    label: 'Proj. cost',
    align: 'right',
    render: (r) => <span className="text-muted-foreground">{nullableMoney(r.projected_cost)}</span>,
  }
  return [nameCol, categoryCol, qty, ...channelCols, avg, gross, net, pctNet, orders, projCost, projMargin, marginPct]
}

function formatCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

function buildItemsPdf(
  items: ItemSalesRow[],
  totals: { qty: number; net: number; margin: number },
  formatCurrency: (n: number) => string,
  groupBy: GroupBy,
  channel: { channels: string[]; labelFor: (id: string) => string },
): string {
  // Same channel columns the screen shows, so a printed report and the page it
  // came from can be laid side by side.
  const chans = groupBy === 'tag' ? [] : channel.channels
  const chanHead = chans.map((id) => `<th class="num">${escapeHtml(channel.labelFor(id))}</th>`).join('')
  const chanCells = (i: ItemSalesRow) =>
    chans.map((id) => `<td class="num">${i.qty_by_channel?.[id] ?? 0}</td>`).join('')
  const colCount = 9 + chans.length
  const firstLabel = groupBy === 'category' ? 'Category' : groupBy === 'tag' ? 'Tag' : 'Item'
  const secondLabel = groupBy === 'tag' ? 'Category' : groupBy === 'category' ? 'Items' : 'Category'
  const netShareLabel = groupBy === 'tag' ? '% of cat' : '% of net'
  const second = (i: ItemSalesRow) =>
    groupBy === 'category' ? String(i.item_count ?? 0) : escapeHtml(i.category ?? '—')
  const netShare = (i: ItemSalesRow) =>
    groupBy === 'tag'
      ? i.share_of_category != null
        ? `${i.share_of_category.toFixed(1)}%`
        : '—'
      : `${i.percent_of_net.toFixed(1)}%`
  const body = items
    .map((i) => {
      const margin = i.projected_margin != null ? escapeHtml(formatCurrency(i.projected_margin)) : '—'
      const marginPct = i.projected_margin_pct != null ? `${i.projected_margin_pct.toFixed(1)}%` : '—'
      return `<tr><td>${escapeHtml(i.name)}</td><td>${second(i)}</td><td class="num">${i.qty_sold}</td>${chanCells(i)}<td class="num">${escapeHtml(formatCurrency(i.gross))}</td><td class="num">${escapeHtml(formatCurrency(i.net))}</td><td class="num">${netShare(i)}</td><td class="num">${i.orders_count}</td><td class="num">${margin}</td><td class="num">${marginPct}</td></tr>`
    })
    .join('')
  return `
    <table>
      <thead><tr><th>${firstLabel}</th><th>${secondLabel}</th><th class="num">Qty</th>${chanHead}<th class="num">Gross</th><th class="num">Net</th><th class="num">${netShareLabel}</th><th class="num">Orders</th><th class="num">Proj. margin</th><th class="num">Margin %</th></tr></thead>
      <tbody>${body || `<tr><td colspan="${colCount}" class="muted">No results</td></tr>`}</tbody>
      <tfoot><tr><th colspan="2">Total</th><th class="num">${totals.qty}</th>${chans.map(() => '<th></th>').join('')}<th></th><th class="num">${escapeHtml(formatCurrency(totals.net))}</th><th></th><th></th><th class="num">${escapeHtml(formatCurrency(totals.margin))}</th><th></th></tr></tfoot>
    </table>
  `
}
